import type { MinimalSubscriptionResponse } from '@talos/kyoko';
import {
  BlotterAggFunc,
  BlotterDensity,
  type Column,
  type ColumnDef,
  columnTypes,
  getAvailableMarginSizeValue,
  getTradingStatus,
  type ICustomerPosition,
  MARGIN,
  useAccordionFilterBuilder,
  useConstant,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type WebsocketRequest,
} from '@talos/kyoko';
import type { ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { map, pipe } from 'rxjs';
import { useMarginFilter } from '../../../../components/Filters';

// Todo update types.ts to get these properties properly into the ICustomerPosition type.
export type MarginPosition = ICustomerPosition & {
  rowID: string;
  Equity?: string;
  NextFundingPayment?: string;
  AvailableMarginToBuy?: string;
  AvailableMarginToSell?: string;
};

export const getColumns = () =>
  [
    { type: 'account', field: 'MarketAccount', initialWidth: 200, initialRowGroup: true, initialHide: true },
    { type: 'asset', field: 'Symbol' },
    {
      type: 'custom',
      id: 'TradingStatus',
      title: 'Trading Status',
      params: {
        valueGetter: ({ data }: ValueGetterParams<MarginPosition>) => getTradingStatus(data),
      },
    },
    {
      type: 'size',
      field: 'Amount',
      params: {
        securityField: 'Symbol',
        securityFieldProperty: 'PositionCurrency',
      },
    },
    {
      type: 'size',
      field: 'OutstandingBuy',
      aggregate: true,
      initialHide: true,
      params: {
        securityField: 'Symbol',
        securityFieldProperty: 'PositionCurrency',
      },
    },
    {
      type: 'size',
      field: 'OutstandingSell',
      aggregate: true,
      initialHide: true,
      params: {
        securityField: 'Symbol',
        securityFieldProperty: 'PositionCurrency',
      },
    },
    {
      type: 'size',
      field: 'AvgCost',
      params: {
        securityField: 'Symbol',
        securityFieldProperty: 'QuoteCurrency',
      },
    },
    {
      type: 'size',
      field: 'MarkPrice',
      params: {
        securityField: 'Symbol',
        securityFieldProperty: 'QuoteCurrency',
      },
    },
    {
      type: 'size',
      field: 'UnrealizedPnL',
      aggregate: true,
      params: {
        securityField: 'Symbol',
        securityFieldProperty: 'QuoteCurrency',
      },
    },
    {
      type: 'size',
      field: 'RealizedPnL',
      aggregate: true,
      params: {
        securityField: 'Symbol',
        securityFieldProperty: 'QuoteCurrency',
      },
    },
    {
      type: 'size',
      field: 'FundingPnL',
      aggregate: true,
      params: {
        securityField: 'Symbol',
        securityFieldProperty: 'QuoteCurrency',
      },
    },
    {
      type: 'size',
      field: 'UsedMargin',
      aggregate: true,
      params: {
        currencyField: 'MarginCurrency',
      },
    },
    {
      type: 'size',
      id: 'AvailableMargin',
      title: 'Available Margin',
      aggregate: true,
      initialAggFunc: BlotterAggFunc.UniformSize,
      params: {
        valueGetter: ({ data, context }: ValueGetterParams<MarginPosition>) =>
          getAvailableMarginSizeValue(data, context),
      },
    },
    {
      type: 'size',
      field: 'AvailableMarginToBuy',
      title: 'Available Margin to Buy',
      params: {
        currencyField: 'MarginCurrency',
      },
    },
    {
      type: 'size',
      field: 'AvailableMarginToSell',
      title: 'Available Margin to Sell',
      params: {
        currencyField: 'MarginCurrency',
      },
    },
    {
      type: 'size',
      field: 'Collateral',
      aggregate: true,
      initialAggFunc: BlotterAggFunc.MaxSize,
      params: {
        currencyField: 'MarginCurrency',
      },
    },
    { type: 'number', field: 'Leverage', params: { currency: 'x' } },
    {
      type: 'size',
      field: 'Equity',
      aggregate: true,
      initialAggFunc: BlotterAggFunc.MaxSize,
      params: {
        currencyField: 'MarginCurrency',
      },
    },
    {
      type: 'size',
      field: 'NextFundingPayment',
      aggregate: true,
      params: {
        currencyField: 'MarginCurrency',
      },
    },
  ] satisfies ColumnDef<MarginPosition>[];

const generateRowID = (position: ICustomerPosition) => `${position.MarketAccount}-${position.Symbol}`;

const marginCompositeIDPipe = pipe(
  map((result: MinimalSubscriptionResponse<ICustomerPosition>) => {
    return {
      ...result,
      data: result.data.map(position => ({
        ...position,
        rowID: generateRowID(position),
      })),
    };
  })
);

interface UseMarginBlotterProps {
  showZeroBalances: boolean;
}

export const useMarginBlotter = ({ showZeroBalances }: UseMarginBlotterProps) => {
  const request = useConstant<WebsocketRequest>({
    name: MARGIN,
    tag: 'BLOTTER_MARGIN',
    ShowZeroBalances: true,
  });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        suppressColumnsToolPanel: true,
        params: { suppressCount: true },
        sortable: true,
        initialWidth: 250,
      }),
    []
  );

  const columns: Column[] = useMemo(() => getColumns(), []);

  const persistedBlotterTable = usePersistedBlotterTable('Margin_Blotter');

  const filteredPositions = useMarginFilter({ persistedBlotterTable, showZeroBalances });
  const { clientSideFilter: clientLocalFilter, filterBuilderProps } = filteredPositions;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const compositePipe = useConstant(marginCompositeIDPipe);

  const blotterTable = useWsBlotterTable<WebsocketRequest, MarginPosition, ICustomerPosition>({
    request,
    rowID: 'rowID',
    density: BlotterDensity.Comfortable,
    persistence: persistedBlotterTable,
    columns,
    gridOptions: { autoGroupColumnDef },
    pipe: compositePipe,
    clientLocalFilter,
  });

  return useMemo(() => ({ blotterTable, filterBuilderAccordion }), [blotterTable, filterBuilderAccordion]);
};
